"use client";

import Link from "next/link";
import { useState, useTransition, type PointerEvent } from "react";
import { addLineItem, openCartDrawer } from "../../../lib/cart-store";
import { wixSized, wixSrcSet } from "../../../lib/media";
import { colorDots, tileName } from "../../../lib/brand-shop";
import type { CatalogLite } from "../../../lib/types";

/**
 * Brand-page product tile.
 *
 * Photos: the lite row carries up to three gallery photos, and moving the
 * pointer across the tile scrubs through them (the 21st.dev "image viewer that
 * follows the pointer's x position" shell, applied to a product card). The
 * extra photos are `hidden md:block`, so phones never download them; keyboard
 * focus shows the angle shot.
 *
 * Buying: in stock -> the same quick-add behaviour as ProductCard (quick add,
 * or "Select <option>" opening the inline panel). Sold out -> the primary
 * action becomes "Ask on WhatsApp" with the product name and URL pre-filled,
 * and a text link under the price carries the same path on touch screens,
 * where there is no hover bar. When stock arrives the normal path returns on
 * its own.
 *
 * The action bar sits beside the link, not inside it: a button or anchor
 * nested in an anchor is invalid HTML and browsers split the nesting.
 */

const TILE_WIDTHS = [360, 540, 720, 960];

export default function BrandTile({
  product,
  askUrl,
  isOpen,
  onToggleQuickAdd,
  priority = false,
}: {
  product: CatalogLite;
  askUrl: string;
  isOpen: boolean;
  onToggleQuickAdd: () => void;
  priority?: boolean;
}) {
  const [idx, setIdx] = useState(0);
  // Extra photos mount on first pointer/focus contact with the tile (then stay),
  // so a 70-tile brand page doesn't download every angle shot up front.
  const [engaged, setEngaged] = useState(false);
  const [pending, startTransition] = useTransition();
  const [added, setAdded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const images = product.images?.length ? product.images : product.image ? [product.image] : [];
  const n = images.length;
  const needsChoice = product.manageVariants || Boolean(product.hasOptions);
  const optionName = product.options?.[0]?.name ?? "Size";
  const { base, colorway } = tileName(product.name);
  const dots = colorway ? colorDots(colorway) : undefined;
  const colorCount = product.options?.find((o) => /colou?r/i.test(o.name))?.choices.length;
  const href = `/shop/${product.slug}`;

  function scrub(e: PointerEvent<HTMLElement>) {
    if (n < 2 || e.pointerType !== "mouse") return;
    setEngaged(true);
    const r = e.currentTarget.getBoundingClientRect();
    const i = Math.floor(((e.clientX - r.left) / r.width) * n);
    setIdx(Math.min(n - 1, Math.max(0, i)));
  }

  function onAction() {
    if (!product.inStock) return;
    if (needsChoice) {
      onToggleQuickAdd();
      return;
    }
    setError(null);
    startTransition(async () => {
      try {
        await addLineItem({ productId: product.id, quantity: 1 });
        setAdded(true);
        openCartDrawer();
        setTimeout(() => setAdded(false), 1800);
      } catch (err) {
        console.error("[quick-add] failed", err);
        setError("Could not add to cart. Please try again.");
      }
    });
  }

  const actionLabel = needsChoice
    ? isOpen
      ? "Close"
      : `Select ${optionName}`
    : added
      ? "Added"
      : pending
        ? "Adding"
        : "Quick Add";

  const barClass =
    "block w-full bg-ink/95 py-2.5 text-center text-[10px] uppercase tracking-[0.15em] text-paper backdrop-blur transition hover:bg-gold-deep focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-paper";

  return (
    <div className={`brand-tile group relative ${product.inStock ? "" : "is-sold-out"}`}>
      <div className="relative aspect-square overflow-hidden bg-panel">
        <Link
          href={href}
          aria-label={product.name}
          className="absolute inset-0 block focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-ink"
          onPointerEnter={(e) => e.pointerType === "mouse" && n > 1 && setEngaged(true)}
          onPointerMove={scrub}
          onPointerLeave={() => setIdx(0)}
          onFocus={() => {
            if (n > 1) {
              setEngaged(true);
              setIdx(1);
            }
          }}
          onBlur={() => setIdx(0)}
        >
          {n > 0 ? (
            images.map((src, i) =>
              i > 0 && !engaged ? null : (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                key={src}
                src={wixSized(src, 540, 540)}
                srcSet={wixSrcSet(src, TILE_WIDTHS, 1)}
                sizes="(min-width: 768px) 25vw, 45vw"
                alt={i === 0 ? product.name : ""}
                aria-hidden={i === 0 ? undefined : true}
                width={540}
                height={540}
                loading={priority && i === 0 ? "eager" : "lazy"}
                decoding="async"
                className={`brand-cutout absolute inset-0 h-full w-full object-cover ${i === 0 ? "" : "hidden md:block"} ${
                  i === idx ? "opacity-100" : "opacity-0"
                }`}
              />
              ),
            )
          ) : (
            <span className="absolute inset-0 flex items-center justify-center text-mist">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" aria-hidden="true">
                <rect x="3" y="3" width="18" height="18" rx="1.5" />
                <circle cx="8.5" cy="8.5" r="1.6" />
                <path d="M21 15l-5-5L5 21" />
              </svg>
            </span>
          )}
          {n > 1 && (
            <span className="brand-tile-steps" aria-hidden="true">
              {images.map((src, i) => (
                <i key={src} data-on={i === idx ? "" : undefined} />
              ))}
            </span>
          )}
        </Link>

        {!product.inStock && (
          <span className="pointer-events-none absolute left-2 top-2 bg-ink/90 px-2 py-1 text-[10px] uppercase tracking-[0.15em] text-paper">
            Sold out
          </span>
        )}

        <div
          className={`absolute inset-x-0 bottom-0 transition-[transform,opacity] duration-300 group-focus-within:translate-y-0 group-focus-within:opacity-100 group-hover:translate-y-0 group-hover:opacity-100 motion-reduce:transition-none ${
            isOpen ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0"
          }`}
        >
          {product.inStock ? (
            <button
              type="button"
              onClick={onAction}
              disabled={pending}
              data-quick-add-trigger={product.id}
              aria-expanded={needsChoice ? isOpen : undefined}
              className={`${barClass} disabled:opacity-60`}
            >
              {actionLabel}
            </button>
          ) : (
            <a href={askUrl} target="_blank" rel="noopener noreferrer" className={barClass}>
              Ask on WhatsApp
            </a>
          )}
        </div>
      </div>

      <div className="mt-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Link href={href} className="block text-[12px] uppercase tracking-wide transition hover:text-gold-deep [overflow-wrap:anywhere]">
            {base}
            {colorway && <span className="text-ink/60"> {colorway}</span>}
          </Link>
          {(dots || colorCount) && (
            <p className="mt-1.5 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] text-ink/60">
              {dots?.map((c) => (
                <span
                  key={c}
                  aria-hidden="true"
                  className="inline-block h-2.5 w-2.5 rounded-full border border-ink/20"
                  style={{ backgroundColor: c }}
                />
              ))}
              {colorCount ? <span>{colorCount} colors</span> : null}
            </p>
          )}
          {!product.inStock && (
            <a
              href={askUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1.5 inline-block text-[10px] uppercase tracking-[0.14em] text-ink/60 underline underline-offset-4 transition hover:text-ink"
            >
              Sold out · Ask about it
            </a>
          )}
        </div>
        <p className="flex shrink-0 items-center gap-2 text-[12px] text-ink/70">
          {product.compareAt && <span className="text-ink/40 line-through">{product.compareAt}</span>}
          {product.price}
        </p>
      </div>
      {error && <p className="mt-1 text-[12px] text-red-700">{error}</p>}
    </div>
  );
}
